/**
 * Super Admin → Maintenance Center (iter309-redesign PR 6)
 *
 * Tabbed shell that consolidates every ops surface: platform health,
 * data integrity checks + guarded repairs, data cleanup tools, cron
 * ledger, billing emails, and the super-admin audit log.
 *
 * Selected tab is persisted to `?tab=X` so deep-links from Slack or
 * runbooks land the operator on the right sub-page. Legacy standalone
 * routes (`/super-admin/billing-emails`, `/cron-ledger`, `/audit-log`)
 * still work — they redirect here.
 */
import { useEffect, useState, lazy, Suspense } from "react";
import { useSearchParams } from "react-router-dom";
import { RETIRED_TOOLS_VISIBLE } from "../../lib/retiredTools";
import {
  Wrench,
  Activity,
  ShieldCheck,
  Mail,
  Clock,
  History,
  Database,
  Loader2,
  ListChecks,
} from "lucide-react";

const DataCleanupTab = lazy(() => import("./maintenance/DataCleanupTab"));
const PlatformHealthTab = lazy(() => import("./maintenance/PlatformHealthTab"));
const DataIntegrityTab = lazy(() => import("./maintenance/DataIntegrityTab"));
const IndexesTab = lazy(() => import("./maintenance/IndexesTab"));
const SuperAdminBillingEmails = lazy(() => import("./SuperAdminBillingEmails"));
const SuperAdminCronLedger = lazy(() => import("./SuperAdminCronLedger"));
const SuperAdminAuditLog = lazy(() => import("./SuperAdminAuditLog"));
const SuperAdminEndpointReport = lazy(() => import("./SuperAdminEndpointReport"));

const TABS = [
  { id: "health", label: "Platform Health", icon: Activity, Component: PlatformHealthTab },
  // iter392 — Data Integrity repairs retired from screens (code kept).
  ...(RETIRED_TOOLS_VISIBLE ? [{ id: "integrity", label: "Data Integrity", icon: ShieldCheck, Component: DataIntegrityTab }] : []),
  { id: "indexes", label: "Indexes", icon: Database, Component: IndexesTab },
  { id: "cleanup", label: "Data Cleanup", icon: Wrench, Component: DataCleanupTab },
  { id: "billing-emails", label: "Billing Emails", icon: Mail, Component: SuperAdminBillingEmails },
  { id: "cron", label: "Cron Ledger", icon: Clock, Component: SuperAdminCronLedger },
  { id: "audit", label: "Audit Log", icon: History, Component: SuperAdminAuditLog },
  { id: "endpoint-report", label: "Endpoint Report", icon: ListChecks, Component: SuperAdminEndpointReport },
];

const DEFAULT_TAB = "health";

export default function SuperAdminMaintenance() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("tab");
  const [tab, setTab] = useState(
    TABS.find((t) => t.id === initial) ? initial : DEFAULT_TAB,
  );

  useEffect(() => {
    // Keep URL in sync so deep-links share.
    const cur = params.get("tab");
    if (cur !== tab) {
      const next = new URLSearchParams(params);
      next.set("tab", tab);
      setParams(next, { replace: true });
    }
  }, [tab, params, setParams]);

  const Active = (TABS.find((t) => t.id === tab) || TABS[0]).Component;

  return (
    <div className="space-y-6" data-testid="super-admin-maintenance-page">
      <header>
        <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
          <Wrench className="w-7 h-7 text-amber-500" />
          Maintenance Center
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Platform health, data integrity, cleanup + operations tooling. Every action is audited.
        </p>
      </header>

      <nav
        className="flex flex-wrap gap-1 border-b border-slate-200"
        data-testid="maintenance-tabs"
      >
        {TABS.map((t) => {
          const active = tab === t.id;
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`maintenance-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                active
                  ? "border-amber-500 text-amber-700"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </nav>

      <div data-testid="maintenance-tab-panel">
        <Suspense
          fallback={
            <div className="flex items-center gap-2 text-slate-500 text-sm py-8">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          }
        >
          <Active />
        </Suspense>
      </div>
    </div>
  );
}
